import type { TripRoute } from "@/types/trip";
import { type MaybeRefOrGetter, toValue } from "vue";
import type {
  DisableRule,
  InvalidReloadTripStop,
  RouteActionsValidationContext,
  TripDispatchValidationContext,
} from "@/types/validation";
import {
  ORDER_STATUSES_WITHOUT_NEW_PAYMENTS,
  OrderFreightState,
  type OrderStatus,
  OrderStopType,
  OrderStopTypeMap,
} from "@/constants/order";
import { isSameEntity } from "@/utils/sameEntity";
import type { RouteStopDto, TravelOrderDto } from "@/api/trips/dto/trip.dto";
import { RouteRedispatchState, RouteStatus, RouteStopStatus } from "@/constants/trip";
import i18n from "@/plugins/i18n";
import { TravelOrderPaymentTypeLabel } from "@/constants/travelOrder";
import { useBaseView, useFreights, useTrip } from "@/composables";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import type { Order, OrderFreightDto } from "@/api/orders/dto/order.dto";

const t = i18n.global.t;

export function useDisableRules() {
  const { getFreightLabel, hasRouteFreightMismatch } = useFreights();
  const { isRouteStopActive, isRouteStopCancelled, isTravelOrderCancelled } = useTrip();

  function createTripDispatchDisableRules(context: MaybeRefOrGetter<TripDispatchValidationContext>): DisableRule[] {
    const { routes: routeOrRoutes, tripStops = [], orderFreights = [], dispatchRequired = false } = toValue(context);

    const routes: TripRoute[] = Array.isArray(routeOrRoutes) ? routeOrRoutes : [routeOrRoutes];

    if (!routes.length) {
      return [{ disabled: true, reasons: [t("trip.add-at-least-one-route")] }];
    }

    function getRoutePosition(route: TripRoute | undefined): string {
      return route ? (routes.findIndex((item) => isSameEntity(item, route)) + 1).toString() : "";
    }

    const travelOrders = routes.flatMap((route) => route.travelOrders);
    const routeFreights = routes.flatMap((route) => route.freights);
    const activeOrderFreights = orderFreights.filter((orderFreight) => !orderFreight.isDeleted);

    const travelOrdersWithoutPayment = travelOrders
      .map((to) => {
        const paymentTypesWithoutAmount = to.payments
          .filter((payment) => !payment.amount && payment.amount !== 0)
          .map(({ type }) => type)
          .filter(Boolean);

        if (!paymentTypesWithoutAmount.length) {
          return null;
        }

        return {
          ...to,
          paymentTypesWithoutAmount,
        };
      })
      .filter(Boolean);

    const activeRoutes = routes.filter((route) => route.status !== RouteStatus.cancelled);
    const routeStops = routes.flatMap((route) => route.routeStops);
    const activeRouteStops = activeRoutes
      .flatMap((route) => route.routeStops)
      .filter((stop) => {
        return isRouteStopActive(stop);
      });

    const activeTravelOrders = activeRoutes.flatMap((route) => route.travelOrders);

    const activeRouteFreights = activeRoutes.flatMap((route) => route.freights);
    const activeRouteFreightsMap = new Map(activeRouteFreights.map((freight) => [freight.id, freight]));

    const orderFreightIdsWithActiveRouteFreight = new Set(
      routeFreights.filter((freight) => !freight.isDeleted).map((freight) => freight.freightId),
    );

    const orderFreightsWithoutRouteFreight = activeOrderFreights.filter((orderFreight) => {
      return !orderFreightIdsWithActiveRouteFreight.has(orderFreight.id!);
    });

    function getOrderFreightLabel(orderFreight: OrderFreightDto): string {
      return getFreightLabel(orderFreight) || `#${orderFreight.number ?? ""}`;
    }

    const createdRouteStopsWithoutFreight = activeRouteStops.filter((stop) => {
      const activeFreights = stop.freights.filter((freightId) => {
        const freight = activeRouteFreightsMap.get(freightId);
        return freight && !freight?.isDeleted;
      });

      return !activeFreights.length;
    });

    const routesWithoutMandatoryStops = routes.filter((route) => {
      const createdDeliveries = route.routeStops.filter(
        (stop) => stop.type === OrderStopType.delivery && stop.use && !stop.isDeleted,
      );
      const createdPickUps = route.routeStops.filter(
        (stop) => stop.type === OrderStopType.pickup && stop.use && !stop.isDeleted,
      );

      return !createdDeliveries.length || !createdPickUps.length;
    });

    const deletedFreightIds = new Set(
      routes
        .flatMap((route) => route.freights)
        .filter((freight) => freight.isDeleted)
        .map((freight) => freight.id),
    );

    const pickUpFreights = routeStops
      .filter((stop) => stop.type === OrderStopType.pickup && !stop.isDeleted)
      .flatMap((stop) => stop.freights)
      .filter((id) => !deletedFreightIds.has(id));

    const deliveriesFreights = routeStops
      .filter((stop) => stop.type === OrderStopType.delivery && !stop.isDeleted)
      .flatMap((stop) => stop.freights)
      .filter((id) => !deletedFreightIds.has(id));

    const deliveryFreightsSet = new Set(deliveriesFreights);
    const freightsMismatch = pickUpFreights.some((f) => !deliveryFreightsSet.has(f));
    const unUpdatedRouteFreights = activeRouteFreights.filter(
      (routeFreight) => routeFreight.state !== OrderFreightState.updated,
    );

    const routeFreightMismatch = hasRouteFreightMismatch(unUpdatedRouteFreights, orderFreights);

    const routesNeededDispatch = routes.filter((route) => !route.isDispatched);

    const tripStopIdsWithActiveRouteStop = new Set(activeRouteStops.map((routeStop) => routeStop.tripStopId));

    const liveTravelOrderByOrderId = new Map<string, TravelOrderDto>();

    activeTravelOrders
      .filter((travelOrder) => !isTravelOrderCancelled(travelOrder))
      .forEach((travelOrder) => {
        if (!liveTravelOrderByOrderId.has(travelOrder.orderId)) {
          liveTravelOrderByOrderId.set(travelOrder.orderId, travelOrder);
        }
      });

    const tripStopsWithoutActiveRouteStop = tripStops.filter((tripStop) => {
      return !tripStop.isDeleted && !tripStopIdsWithActiveRouteStop.has(tripStop.id);
    });

    const tripStopsWithLiveTravelOrder = tripStopsWithoutActiveRouteStop
      .filter((tripStop) => !tripStop.isReload)
      .map((tripStop) => {
        const orderId = tripStop.order?.id;

        return {
          tripStop,
          travelOrder: orderId ? liveTravelOrderByOrderId.get(orderId) : undefined,
        };
      })
      .filter(({ travelOrder }) => Boolean(travelOrder));

    const tripStopIdsWithDeletedRouteStop = new Set(
      routeStops.filter((routeStop) => routeStop.isDeleted).map((routeStop) => routeStop.tripStopId),
    );

    const tripStopIdsWithLiveTravelOrder = new Set(tripStopsWithLiveTravelOrder.map(({ tripStop }) => tripStop.id));

    const tripStopsWithDeletedRouteStop = tripStopsWithoutActiveRouteStop.filter((tripStop) => {
      return tripStopIdsWithDeletedRouteStop.has(tripStop.id) && !tripStopIdsWithLiveTravelOrder.has(tripStop.id);
    });

    const tripStopsRequiringActiveRouteStop = [
      ...tripStopsWithLiveTravelOrder.map(({ tripStop }) => tripStop),
      ...tripStopsWithDeletedRouteStop,
    ];

    const invalidReloadTripStops = tripStops.flatMap<InvalidReloadTripStop>((tripStop) => {
      if (!tripStop.isReload || tripStop.isDeleted) {
        return [];
      }

      const relatedReloadRouteStops = activeRouteStops.filter((routeStop) => {
        return routeStop.isReload && routeStop.tripStopId === tripStop.id;
      });

      const hasPickUp = relatedReloadRouteStops.some((routeStop) => routeStop.type === OrderStopType.pickup);
      const hasDelivery = relatedReloadRouteStops.some((routeStop) => routeStop.type === OrderStopType.delivery);

      if (hasPickUp && hasDelivery) {
        return [];
      }

      return [{ number: tripStop.number, hasPickUp, hasDelivery }];
    });

    function getInvalidReloadTripStopReason({ number, hasPickUp, hasDelivery }: InvalidReloadTripStop): string {
      if (!hasPickUp && !hasDelivery) {
        return t("trip.reload-trip-stop-needs-pickup-delivery", { number });
      }

      return hasPickUp
        ? t("trip.reload-trip-stop-needs-delivery", { number })
        : t("trip.reload-trip-stop-needs-pickup", { number });
    }

    return [
      {
        disabled: Boolean(travelOrdersWithoutPayment.length),
        reasons: travelOrdersWithoutPayment.flatMap((item) => {
          return (item?.paymentTypesWithoutAmount ?? []).map((paymentType) => {
            return `${t("trip.add-rate-for-to", { rateType: TravelOrderPaymentTypeLabel[paymentType!] })} ${item?.name}`;
          });
        }),
      },
      {
        disabled: Boolean(routesWithoutMandatoryStops.length),
        reasons: routesWithoutMandatoryStops.map(
          (route) => `${t("trip.add-at-least-one-pickup-delivery-route")} #${getRoutePosition(route)}`,
        ),
      },
      {
        disabled: Boolean(createdRouteStopsWithoutFreight.length),
        reasons: [
          t("trip.add-route-freight"),
          ...createdRouteStopsWithoutFreight.map(
            (stop) => `${OrderStopTypeMap[stop.type]} #${stop.number} ${t("general.stop").toLowerCase()}`,
          ),
        ],
      },
      {
        disabled: freightsMismatch,
        reasons: [t("trip.each-freight-pick-up-delivery")],
      },
      {
        disabled: dispatchRequired ? Boolean(orderFreightsWithoutRouteFreight.length) : false,
        reasons: orderFreightsWithoutRouteFreight.map((orderFreight) =>
          t("trip.order-freight-needs-route-freight", { freight: getOrderFreightLabel(orderFreight) }),
        ),
      },
      {
        disabled: routeFreightMismatch,
        reasons: [t("trip.update-route-freight-details")],
      },
      {
        disabled: dispatchRequired ? Boolean(tripStopsRequiringActiveRouteStop.length) : false,
        reasons: tripStopsRequiringActiveRouteStop.map((tripStop) =>
          t("trip.trip-stop-has-no-active-route-stops", { stop: tripStop.name }),
        ),
      },
      {
        disabled: dispatchRequired ? Boolean(invalidReloadTripStops.length) : false,
        reasons: invalidReloadTripStops.map(getInvalidReloadTripStopReason),
      },
      {
        disabled: dispatchRequired ? Boolean(routesNeededDispatch.length) : false,
        reasons: routesNeededDispatch.map(
          (route) => `${t("general.route")} #${getRoutePosition(route)} ${t("trip.not-dispatched")}`,
        ),
      },
    ];
  }

  function createAddCheckCallDisableRules(
    confirmDispatch: boolean,
    nextStop: MaybeRefOrGetter<RouteStopDto | undefined>,
    routeStatus: RouteStatus,
  ): DisableRule[] {
    const nextStopRaw = toValue(nextStop);
    const { hasAccess } = useBaseView();

    const checkedInStatuses = [RouteStopStatus.waitingForLoading, RouteStopStatus.waitingForGoodToGo];
    const isDriverCheckedIn = nextStopRaw ? checkedInStatuses.includes(nextStopRaw.status) : false;

    return [
      {
        disabled: !hasAccess(DispatcherPermissionValue.CheckCallsEdit),
        reasons: [t("permission.create-check-call")],
      },
      {
        disabled: !confirmDispatch,
        reasons: [t("trip.check-call-not-confirmed")],
      },
      {
        disabled: isDriverCheckedIn,
        reasons: [t("trip.check-call-driver-checked-in")],
      },
      {
        disabled: routeStatus !== RouteStatus.inProgress,
        reasons: [t("trip.check-call-route-not-in-progress")],
      },
    ];
  }

  function createRouteActionsDisableRules(context: MaybeRefOrGetter<RouteActionsValidationContext>): DisableRule[] {
    const { route, stop } = toValue(context);
    const { hasAccess } = useBaseView();

    return [
      {
        disabled: !hasAccess(DispatcherPermissionValue.TripsRouteActions),
        reasons: [t("permission.process-trip")],
      },
      {
        disabled: isRouteStopCancelled(route, stop),
        reasons: [t("trip.route-actions-stop-cancelled")],
      },
      {
        disabled: route.redispatch === RouteRedispatchState.notConfirmed,
        reasons: [t("trip.route-actions-redispatch-not-confirmed")],
      },
      {
        disabled: route.redispatch === RouteRedispatchState.needed,
        reasons: [t("trip.route-actions-redispatch-needed")],
      },
    ];
  }

  function createAddTruckToRouteDisableRules(route: MaybeRefOrGetter<TripRoute>): DisableRule[] {
    const routeRaw = toValue(route);
    const hasAtLeastOneActivePickUp = routeRaw.routeStops.some(
      (routeStop) => routeStop.use && routeStop.type === OrderStopType.pickup,
    );
    return [
      {
        disabled: !hasAtLeastOneActivePickUp,
        reasons: [t("trip.add-at-least-one-pickup")],
      },
    ];
  }

  function createSaveOrderDisableRules(order: MaybeRefOrGetter<Order>): DisableRule[] {
    const orderRaw = toValue(order);

    if (!orderRaw?.orderStops || !orderRaw.freights) {
      return [];
    }

    const freights = orderRaw.freights;
    const freightsById = new Map(freights.map((freight) => [freight.id, freight]));

    const missingFreights = freights
      .filter((freight) => !freight.isDeleted && !freight.$usedIn?.length)
      .map((freight) => `Freight${freight.$number}`);

    const stopsWithAllDeletedFreights = orderRaw.orderStops
      .filter((stop) => {
        const stopFreights = stop.freights ?? [];

        if (!stopFreights.length || stop.isDeleted) {
          return false;
        }

        return stopFreights.every((id) => freightsById.get(id)?.isDeleted);
      })
      .map((stop) => `${OrderStopTypeMap[stop.type]} #${stop.$number}`);

    return [
      {
        disabled: missingFreights.length > 0,
        reasons: [t("orders.add-freights-to-delivery-stop", { freights: missingFreights.join(", ") })],
      },
      {
        disabled: stopsWithAllDeletedFreights.length > 0,
        reasons: [t("orders.add-freight-to-stop", { stops: stopsWithAllDeletedFreights.join(", ") })],
      },
    ];
  }

  function createAddOrderPaymentDisableRules(orderStatus: MaybeRefOrGetter<OrderStatus | undefined>): DisableRule[] {
    const status = toValue(orderStatus);

    return [
      {
        disabled: status !== undefined && ORDER_STATUSES_WITHOUT_NEW_PAYMENTS.includes(status),
        reasons: [t("order-payments.add-payment-unavailable")],
      },
    ];
  }

  return {
    createTripDispatchDisableRules,
    createAddCheckCallDisableRules,
    createRouteActionsDisableRules,
    createAddTruckToRouteDisableRules,
    createSaveOrderDisableRules,
    createAddOrderPaymentDisableRules,
  };
}
